/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'components/atoms/Button'
import Checkbox from 'components/atoms/IndeterminateCheckbox'

interface GeneratePatientStatusReportModalProps {
  isOpen: boolean
  onClose: () => void
  faxNumber?: string
  onGenerate: (
    dateRangeStart: Date,
    dateRangeEnd: Date,
    shouldFax: boolean,
    faxNumber: string,
  ) => void
  isGenerating: boolean
}

export const GeneratePatientStatusReportModal = ({
  isOpen,
  onClose,
  faxNumber,
  onGenerate,
  isGenerating,
}: GeneratePatientStatusReportModalProps) => {
  // Default to previous week (Monday to Sunday)
  const getDefaultDates = () => {
    const today = new Date()
    const dayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, etc.
    const daysToLastMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
    const lastMonday = new Date(today)
    lastMonday.setDate(today.getDate() - daysToLastMonday - 7)
    lastMonday.setHours(0, 0, 0, 0)

    const lastSunday = new Date(lastMonday)
    lastSunday.setDate(lastMonday.getDate() + 6)
    lastSunday.setHours(23, 59, 59, 999)

    return {
      start: lastMonday.toISOString().split('T')[0],
      end: lastSunday.toISOString().split('T')[0],
    }
  }

  const defaultDates = getDefaultDates()
  const [dateRangeStart, setDateRangeStart] = useState(defaultDates.start)
  const [dateRangeEnd, setDateRangeEnd] = useState(defaultDates.end)
  const [shouldFax, setShouldFax] = useState(false)
  const [faxNumberValue, setFaxNumberValue] = useState(faxNumber || '')

  useEffect(() => {
    setFaxNumberValue(faxNumber || '')
  }, [faxNumber, isOpen])

  const handleGenerate = () => {
    // Convert to ISO string with time
    const startDate = new Date(dateRangeStart)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(dateRangeEnd)
    endDate.setHours(23, 59, 59, 999)

    onGenerate(startDate, endDate, shouldFax, faxNumberValue)
  }

  return (
    <Modal show={isOpen} onHide={onClose} centered size="lg">
      <Modal.Header>
        <Modal.Title>Generate Patient Status Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-group mt-3">
          <label htmlFor="dateRangeStart">Start Date</label>
          <input
            type="date"
            id="dateRangeStart"
            className="form-control"
            value={dateRangeStart}
            onChange={(e) => setDateRangeStart(e.target.value)}
          />
        </div>
        <div className="form-group mt-3">
          <label htmlFor="dateRangeEnd">End Date</label>
          <input
            type="date"
            id="dateRangeEnd"
            className="form-control"
            value={dateRangeEnd}
            onChange={(e) => setDateRangeEnd(e.target.value)}
          />
        </div>
        <div className="form-group mt-3">
          <label className="d-flex align-items-center">
            <Checkbox checked={shouldFax} onChange={(e) => setShouldFax(e.target?.checked)} />
            <span className="ml-2">Fax report to physician</span>
          </label>
          <small className="form-text text-muted">
            If checked, the report will be automatically faxed to the physician's fax number after
            generation.
          </small>
        </div>
        <div
          className="form-group mt-3"
          style={{
            opacity: shouldFax ? 1 : 0.5,
          }}>
          <label htmlFor="faxNumber">Fax Number</label>
          <input
            type="text"
            id="faxNumber"
            className="form-control"
            value={faxNumberValue}
            disabled={!shouldFax}
            onChange={(e) => setFaxNumberValue(e.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="btn btn-secondary"
          label="Cancel"
          onClick={onClose}
          disabled={isGenerating}
        />
        <Button
          className="btn btn-primary"
          label={isGenerating ? 'Generating...' : 'Generate Report'}
          onClick={handleGenerate}
          disabled={isGenerating || !dateRangeStart || !dateRangeEnd}
        />
      </Modal.Footer>
    </Modal>
  )
}
